import { EVENT_FIELDS, EVENT_TYPES, type EventType, METHODOLOGY, METRICS } from "@atzmai/schema";
import { useApi } from "../../lib/useApi.ts";

interface Status {
  generated_at: string;
  active: { day: string; active_7d: number; active_30d: number; installs: number } | null;
  versions: { release: string; devices: number }[];
  releases: { channel: string; version: string; rollout_pct: number; status: string }[];
}

/** What each field means, in plain words. A field the schema gains before
 *  this list does is still shown, by its own name: nothing is left off. */
export const FIELD: Record<string, string> = {
  schema: "The report format's version",
  id: "A random number for this one report, so it is never counted twice",
  device: "A random ID made on the computer. It isn't linked to you or your email unless you activate the computer with a code",
  ts: "When the report was made",
  type: "Which kind of report this is",
  release: "The Atzmai OS version",
  channel: "Stable or testers",
  model: "The computer's model name",
  arch: "The kind of processor",
  source: "How it was installed: a USB stick, the network, or already on the computer",
  installer: "The installer's version",
  locale: "The language chosen",
  uptime_h: "Hours since it was switched on",
  disk_free_pct: "How much of the disk is free, as a percentage",
  power: "Mains, battery or solar",
  net: "Whether it's online",
  pending_updates: "How many updates are waiting",
  current: "The version it runs",
  available: "The newest version it found, if any",
  from: "The version it updates from",
  to: "The version it updates to",
  bytes: "The update's size",
  seconds: "How long it took",
  mirror: "Which download server it used",
  resumed: "Whether the download picked up after a break",
  delta: "Whether only the changes were downloaded",
  method: "How the update arrived or was applied",
  free_space: "How much of the disk is free, as a percentage",
  stage: "The step that went wrong",
  reason_code: "A short code for why, such as no_space",
  boot_count: "How many times it has started",
  last_release: "The version it ran last",
  watchdog: "Whether it restarted itself after hanging",
  signature: "A fingerprint of the crash, never the crash itself",
  package: "The program that crashed",
  package_version: "That program's version",
  count: "How many times",
  feature_id: "Which Atzmai feature, by its name",
  bundle_id: "Which offline bundle",
  rating: "Your stars, from 1 to 5",
  category: "The topic you picked",
  message: "What you wrote. It is the only free text any report can hold",
  contact_ok: "Whether we may reply to you",
};

const EVENT: Record<EventType, string> = {
  "install.first_boot": "It started for the first time",
  "session.heartbeat": "It's still in use",
  "update.check": "It looked for updates",
  "update.download": "It downloaded an update",
  "update.apply_start": "It began installing an update",
  "update.apply_ok": "An update finished",
  "update.apply_fail": "An update failed",
  "update.rollback": "An update was undone",
  "boot.ok": "It started up",
  "boot.fail": "It couldn't start",
  crash: "A program crashed",
  "feature.use": "A feature was used",
  "offline.import": "It took updates from a USB stick or the local network",
  feedback: "You sent feedback",
};

export const GROUPS: { title: string; types: EventType[] }[] = [
  { title: "Once, when it's new", types: ["install.first_boot"] },
  { title: "While it's on", types: ["session.heartbeat", "boot.ok", "feature.use"] },
  { title: "When it updates", types: ["update.check", "update.download", "update.apply_start", "update.apply_ok", "offline.import"] },
  { title: "When something goes wrong", types: ["update.apply_fail", "update.rollback", "boot.fail", "crash"] },
  { title: "Only when you choose to", types: ["feedback"] },
];

const NEVER = [
  "Your documents, their names or what's in them",
  "What you type, search for or browse",
  "Window titles, or what you do in an app",
  "Your name, usernames, contacts or Wi-Fi names",
  "Your IP address (only the country, at first boot)",
  "Where you are, beyond that country",
];

const METRIC: Record<keyof typeof METRICS, string> = {
  downloads: "Downloads",
  installs: "Installs",
  active_7d: "Active this week",
  active_30d: "Active this month",
  silent: "Gone quiet",
  update_success: "Updates that worked",
  rollback_rate: "Updates undone",
  crash_free: "Free of crashes",
  activation_rate: "Activated",
  feedback_closure: "Feedback answered",
};

/** One real heartbeat, whole: every line it can have, and what each says. */
const SPECIMEN: [string, string, string][] = [
  ["schema", "1", FIELD.schema!],
  ["id", '"b1e0…"', FIELD.id!],
  ["device", '"3f9c…"', FIELD.device!],
  ["ts", '"2026-10-24T09:12:00Z"', FIELD.ts!],
  ["type", '"session.heartbeat"', "A heartbeat: the computer is still in use"],
  ["release", '"2026.10.1"', FIELD.release!],
  ["channel", '"stable"', FIELD.channel!],
  ["uptime_h", "6", "On for six hours"],
  ["disk_free_pct", "41", "41% of the disk is free"],
  ["power", '"battery"', "Running on its battery"],
  ["net", '"up"', "Online"],
  ["pending_updates", "0", "No updates waiting"],
];
const PAYLOAD_FROM = SPECIMEN.findIndex(([k]) => k === "uptime_h");

export function Transparency() {
  const { data } = useApi<Status>("/v1/status");
  return (
    <div className="flex flex-col gap-24">
      <header className="flex max-w-[42rem] flex-col gap-4 pt-4 md:pt-10">
        <h1 className="text-[clamp(36px,5.2vw,56px)] leading-[1.06] tracking-[-.02em]">What your computer tells us</h1>
        <p className="text-[20px] leading-snug text-ink-2">
          Atzmai OS can report how the computer is doing, never what you do on it. Nothing is sent until you say yes, and updates work the same either way.
        </p>
      </header>

      <section className="flex flex-col gap-6" aria-labelledby="specimen">
        <div className="flex max-w-[40rem] flex-col gap-2">
          <h2 id="specimen" className="text-[clamp(24px,3vw,32px)] leading-tight">This is a whole report</h2>
          <p className="text-ink-2">A heartbeat, the report sent most often, with every line it can hold. The server turns away any report with a line it doesn't expect.</p>
        </div>
        <div className="specimen overflow-hidden rounded-[22px] bg-[#0a0c27] shadow-[inset_0_0_0_1px_rgb(255_255_255/.09)]">
          {SPECIMEN.map(([key, value, meaning], i) => (
            <div key={key} className={`specimen-row${i === PAYLOAD_FROM ? " specimen-split" : ""}`}>
              <code className={`font-mono text-[14px] text-ink-2 ${i >= PAYLOAD_FROM ? "md:pl-5" : ""}`}>
                <span className="text-planet">{key}</span>: <span className="text-ring">{value}</span>
              </code>
              <span className="text-[16px] text-ink">{meaning}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="grid gap-12 md:grid-cols-2 md:gap-16">
        <div className="flex flex-col gap-4">
          <h2 className="text-[clamp(24px,3vw,32px)] leading-tight">Never sent</h2>
          <ul className="m-0 flex list-none flex-col p-0">
            {NEVER.map((n) => (
              <li key={n} className="flex items-baseline gap-3 border-t border-line py-3 text-ink-2">
                <span aria-hidden="true" className="relative top-[-.1em] inline-block size-2.5 flex-none rounded-full border-2 border-bad/80" />
                {n}
              </li>
            ))}
          </ul>
        </div>
        <div className="flex flex-col gap-4">
          <h2 className="text-[clamp(24px,3vw,32px)] leading-tight">Turning it off</h2>
          <p className="text-ink-2">
            The first screen asks before anything is sent, and the same switch is in Settings. Turning it off stops every report at once.
          </p>
          <p className="text-ink-2">
            Resetting the computer to how it came asks us to delete everything linked to its old ID, and it makes a new one. Your download email is never linked to a computer unless you activate it with a code.
          </p>
        </div>
      </section>

      <section className="flex flex-col gap-8" aria-labelledby="every">
        <div className="flex max-w-[40rem] flex-col gap-2">
          <h2 id="every" className="text-[clamp(24px,3vw,32px)] leading-tight">Every report a computer can send</h2>
          <p className="text-ink-2">
            There are {EVENT_TYPES.length}, and no others. Each carries the heartbeat's first seven lines, plus its own. Open one to see them.
          </p>
        </div>
        <div className="flex flex-col gap-10">
          {GROUPS.map((g) => (
            <div key={g.title} className="grid gap-3 md:grid-cols-[14rem_minmax(0,1fr)] md:gap-10">
              <h3 className="text-[18px] text-ink-2 md:pt-3.5">{g.title}</h3>
              <div className="flex flex-col">
                {g.types.map((t) => (
                  <details key={t} className="report border-t border-line">
                    <summary className="flex cursor-pointer list-none flex-wrap items-baseline justify-between gap-x-4 py-3.5">
                      <span className="text-[17px] text-ink">{EVENT[t]}</span>
                      <code className="font-mono text-[13px] text-ink-3">{t}</code>
                    </summary>
                    <dl className="m-0 grid gap-x-6 gap-y-2 pb-5 sm:grid-cols-[11rem_minmax(0,1fr)]">
                      {EVENT_FIELDS[t].map((f) => (
                        <div key={f} className="contents">
                          <dt className="font-mono text-[13px] text-planet sm:pt-0.5">{f}</dt>
                          <dd className="m-0 text-[15px] text-ink-2">{FIELD[f] ?? f}</dd>
                        </div>
                      ))}
                    </dl>
                  </details>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="grid gap-10 md:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] md:gap-16">
        <div className="flex flex-col gap-4">
          <h2 className="text-[clamp(24px,3vw,32px)] leading-tight">How many computers report</h2>
          <Count status={data} />
        </div>
        <div className="flex flex-col gap-3">
          <p className="text-ink-2">{METHODOLOGY}</p>
          <dl className="m-0 flex flex-col">
            {(Object.keys(METRICS) as (keyof typeof METRICS)[]).map((k) => (
              <div key={k} className="grid gap-x-6 border-t border-line py-2.5 sm:grid-cols-[11rem_minmax(0,1fr)]">
                <dt className="font-display text-[15px] font-medium text-ink">{METRIC[k]}</dt>
                <dd className="m-0 text-[15px] text-ink-2">{METRICS[k]}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>
    </div>
  );
}

function Count({ status }: { status: Status | null }) {
  const a = status?.active;
  if (!status) return <p className="text-ink-3">Counting…</p>;
  if (!a || a.installs === 0) {
    return <p className="text-[20px] leading-snug text-ink">No computer has reported yet. The numbers start with the first one that says yes.</p>;
  }
  const n = (v: number) => <b className="font-display font-medium tabular-nums text-ring">{v.toLocaleString()}</b>;
  return (
    <p className="text-[20px] leading-snug text-ink">
      {n(a.active_7d)} {a.active_7d === 1 ? "computer" : "computers"} reported this week, and {n(a.active_30d)} this month. {n(a.installs)} have reported since the start.
    </p>
  );
}
